import type { AppConfig } from '@/config';
import type { RuntimeConfigKey } from '@/config';

/**
 * The configuration keys authentication cannot function without. API_URL is
 * deliberately absent: nothing calls the API until Chunk 2, so only the
 * Keycloak settings are a hard failure here.
 */
export const AUTH_CONFIG_KEYS: readonly RuntimeConfigKey[] = [
  'KEYCLOAK_BASE_URL',
  'KEYCLOAK_REALM',
  'KEYCLOAK_CLIENT_ID',
  'REDIRECT_URI',
];

/** Keycloak's OIDC issuer for a realm; discovery lives underneath it. */
export function authority(config: AppConfig): string {
  return `${config.keycloakBaseUrl}/realms/${config.keycloakRealm}`;
}

/**
 * The sessionStorage key oidc-client-ts stores the signed-in user under.
 * Shared between the provider (which writes it) and `token.ts` (which reads it
 * outside React for API calls).
 */
export function userStorageKey(config: AppConfig): string {
  return `oidc.user:${authority(config)}:${config.keycloakClientId}`;
}

/**
 * Settings for `react-oidc-context`'s AuthProvider: authorization-code + PKCE
 * against the public `dashboard` client. `oidc-client-ts` defaults to the code
 * flow with PKCE for a client without a secret, so only the endpoints, scope
 * and renewal behaviour are spelled out.
 *
 * No `offline_access` scope and no per-account renewal timers here: WP-04's
 * spike could not confirm the `dashboard` client is granted `offline_access`
 * or that a second account's `prompt=login` leaves the first account's
 * refresh token usable (no live Keycloak credentials in that environment —
 * see PLAN-FASE1.md "Meerdere accounts › Uitkomst spike"). WP-07 therefore
 * builds multiple accounts on the documented fallback (`login_hint`
 * re-authentication in `useSession.ts`), which needs no change here.
 */
export function buildOidcSettings(config: AppConfig) {
  return {
    authority: authority(config),
    client_id: config.keycloakClientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: window.location.origin,
    scope: 'openid profile email',
    // Renew in the background off the refresh token Keycloak issues, so a
    // working session never bounces through a visible redirect.
    automaticSilentRenew: true,
  };
}
